import { defineStore } from 'pinia'
import { Auth0 } from '@/config/auth'
import { isBefore } from 'date-fns'
import axios from 'axios'
import { useRouter } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { createAxiosAuthInstance, createXSRFInstance } from '@/config/auth'

type AuthStoreState = {
  accessToken?: string
  accessTokenExpiry?: number
  actorId?: string
  userId?: string
  features?: { [x: string]: boolean }
}

export const useAuthStore = defineStore('authentication', {
  state: (): AuthStoreState => {
    return {
      accessToken: undefined,
      accessTokenExpiry: undefined,
      actorId: undefined,
      userId: undefined,
      features: undefined
    }
  },
  actions: {
    getFeatureValue(feature: string): boolean {
      if (this.features && feature in this.features) {
        return this.features[feature]
      } else {
        return false
      }
    },
    async expiredPassword(email: string) {
      const resetPasswordEmailResponse = this.sendResetPasswordEmail(email)
      return resetPasswordEmailResponse.then(() => {
        Auth0.logout({
          logoutParams: {
            returnTo: `${window.location.origin}/expired`
          }
        })
      })
    },
    async sendResetPasswordEmail(userEmail: string) {
      const changePasswordRemote = axios({
        method: 'post',
        url: `https://${import.meta.env.VITE_VUE_APP_AUTH0_DOMAIN}/dbconnections/change_password`,
        data: {
          client_id: import.meta.env.VITE_VUE_APP_AUTH0_CLIENT_ID,
          connection: import.meta.env.VITE_VUE_APP_AUTH0_CONNECTION,
          email: userEmail
        }
      }).then((response) => response.data)

      // const changePasswordRemote = Promise.resolve()

      await changePasswordRemote
    },
    async checkToken() {
      if (this.accessToken && this.accessTokenExpiry) {
        const willExpireSoon = isBefore(this.accessTokenExpiry, Date.now() + 30)
        if (!willExpireSoon) {
          return {
            accessToken: this.accessToken,
            accessTokenExpiry: this.accessTokenExpiry,
            fromCache: true
          }
        }
      }
      const tokenResponse = await Auth0.getAccessTokenSilently({ detailedResponse: true })
      this.accessToken = tokenResponse.access_token
      this.accessTokenExpiry = Date.now() + tokenResponse.expires_in * 1000
      await this.getProfile()
      return {
        accessToken: this.accessToken,
        accessTokenExpiry: this.accessTokenExpiry,
        fromCache: false
      }
    },
    async trackUser() {
      const currentUser = Auth0.user.value
      if (currentUser) {
        try {
          const axios = createAxiosAuthInstance(
            this.accessToken || '',
            this.actorId || ''
          )(createXSRFInstance())
          const userTracking = await axios({
            method: 'post',
            url: '/api/user_tracking',
            data: {
              id: currentUser.sub
            }
          })
          return userTracking.data
        } catch (error) {
          Sentry.captureException(error)
        }
      }
    },
    async getProfile() {
      const currentUser = Auth0.user.value
      if (currentUser) {
        this.userId = currentUser.sub
        return currentUser
      } else {
        await this.checkToken()
        const newUser = Auth0.user.value
        this.userId = newUser?.sub
        return newUser
      }
    },
    checkErrorForExpired() {
      const { error: errorRef } = useAuth0()
      const router = useRouter()
      if (errorRef.value === null) return
      const isPasswordExpiredError = () => {
        const error = errorRef.value.error_description
        let hasExpiredError = false
        if (error) {
          if (Array.isArray(error)) {
            hasExpiredError = error.join('|').toLowerCase().includes('password expired')
          } else {
            hasExpiredError = error.toLowerCase().includes('password expired')
          }
        }
        return hasExpiredError
      }
      const findEmail = () => {
        const queryParams = errorRef.value
        let email: string | undefined
        email = typeof queryParams.email === 'string' ? queryParams.email : undefined
        if (email) {
          return email
        }
        let error_description = undefined
        if (typeof queryParams.error_description === 'string') {
          error_description = queryParams.error_description.toLowerCase()
          email = error_description.split('email: ')[1]
        }
        if (Array.isArray(queryParams.error_description)) {
          error_description = queryParams.error_description.join('|').toLowerCase()
          email = error_description.split('email: ')[1]
        }
        if (email) {
          return email
        } else {
          const errorThrow = new Error(
            'Tried to expire password with empty or list of emails, only supports single email'
          )
          Sentry.captureException(errorThrow)
          throw errorThrow
        }
      }
      if (isPasswordExpiredError()) {
        const email = findEmail()
        this.expiredPassword(email)
        router.push({ name: 'expired' })
        return
      }
    }
  },
  getters: {
    getTrackedFeatures(state) {
      if (state.features) {
        return {
          get_contact_info: state.features.get_contact_info,
          get_contact_info_personal: state.features.get_contact_info_personal
        }
      }
    }
  }
})
